import React, { useState, useCallback, useEffect } from 'react';
import { useCreate, HttpError, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router-dom';
import {
  UsersIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { semanticColors } from '../../theme';
import { Alert, Avatar, Button, Card, Checkbox, Modal } from 'flowbite-react';
import { Text } from '../../components/text';
import { Badge } from '../../components/badge';
import { CreateNewShift, ShiftDetailsForFacilityResponse } from '../../shared/types/shift';
import {
  formatShiftTime,
  parseISO,
  format,
  differenceInHours,
  isBefore,
  addDays,
} from '../../shared/utils/dateUtils';
import { subMinutes, isAfter } from 'date-fns';
import { getPositionStyles } from '../../shared/utils/uiUtils';
import { getDisplayStatus, hasApplicants } from '../../shared/utils/get-status-color';
import Loading from '../../components/loading/loading';
import PostShiftModal from './shift-drawer';
import ApplicationRow from './application-row'; // Assuming this component exists
import ShiftModal from '../../components/shiftDetailPage';
import { IClinicianShift, IClinicianProfile, IDocumentStatusSummary } from '../../interfaces/types';
import useApiService from '../../hooks/useApiService';

const defaultDocumentStatusSummary: IDocumentStatusSummary = {
  verified: 0,
  pending: 0,
  rejected: 0,
  missing: 0,
  uploaded: 0,
  mandatory_completed: 0,
  mandatory_pending: 0,
  mandatory_total: 0,
  approved: 0,
};

/* --------------------------------------------------------------- */
/* Helper – is the shift start time already in the past?            */
const isShiftStartPassed = (shift: ShiftDetailsForFacilityResponse) => {
  if (!shift?.start_time) return false;
  const now = new Date();
  const start = parseISO(shift.start_time);
  return isBefore(start, now); // true → start time passed
};

/* --------------------------------------------------------------- */
/* Cancellation modal                                               */
interface ShiftCancellationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  shift: ShiftDetailsForFacilityResponse;
  refetchData: () => Promise<unknown>;
}

const ShiftCancellationModal = ({
  open,
  setOpen,
  shift,
  refetchData,
}: ShiftCancellationModalProps) => {
  const [cancellationList, setCancellationList] = useState<string[]>([]);
  const { mutate: cancelShift, isLoading: isCancelling } = useUpdate<
    ShiftDetailsForFacilityResponse,
    HttpError
  >({
    resource: `shifts/cancel`,
    id: shift?.shift_id,
    values: cancellationList,
    mutationOptions: {
      onSuccess: async () => {
        onClose();
        await refetchData();
      },
    },
  });

  const handleSetCancellationList = (openingId: string, checked: boolean) => {
    if (!checked && cancellationList.includes(openingId)) {
      setCancellationList(cancellationList.filter((id) => id !== openingId));
    } else if (checked) {
      setCancellationList([...cancellationList, openingId]);
    }
  };

  const onClose = () => {
    setCancellationList([]);
    setOpen(false);
  };

  const handleProcessCancellations = () => {
    cancelShift();
  };

  const isCancelled = shift.opening_status === 'CANCELLED';
  const isInProgress =
    shift.opening_status === 'CLOCKED_IN' ||
    shift.opening_status?.toLowerCase().includes('progress');
  const isWorked = shift.opening_status === 'COMPLETED';
  const isExpired = shift.opening_status === 'EXPIRED';

  const now = new Date();
  const shiftStartTime = parseISO(shift.start_time);
  const isShiftInPast = isBefore(shiftStartTime, now);

  const cannotCancel =
    isCancelled || isInProgress || isWorked || isExpired || isShiftInPast;

  return (
    <Modal show={open} size="2xl" onClose={() => setOpen(false)} popup>
      <Modal.Header className="border-b border-[#C84648] bg-[#FFEAEA] p-3">
        <Text style={{ fontSize: 20, color: '#C84648' }}>
          Cancel Shift Opening
        </Text>
      </Modal.Header>

      <Modal.Body className="p-4">
        {cannotCancel ? (
          <Alert
            className="mb-4 border border-[#C84648] bg-[#FFEAEA] text-[#C84648]"
            icon={ExclamationTriangleIcon}
          >
            <Text className="text-sm">
              <strong>Cannot Cancel:</strong>{' '}
              {isCancelled
                ? 'This shift is already cancelled.'
                : isInProgress
                ? 'Cannot cancel a shift that is currently in progress.'
                : isWorked
                ? 'Cannot cancel a shift that has been completed.'
                : isExpired
                ? 'Cannot cancel an expired shift.'
                : isShiftInPast
                ? 'Cannot cancel a shift that has already started.'
                : 'This shift cannot be cancelled.'}
            </Text>
          </Alert>
        ) : (
          <Alert
            className="mb-4 border border-[#C84648] bg-[#FFEAEA] text-[#C84648]"
            icon={ExclamationTriangleIcon}
          >
            <Text className="text-sm">
              <strong>Warning:</strong> Canceling a shift opening is
              irreversible. This will remove assigned clinicians and close all
              applications immediately.
            </Text>
          </Alert>
        )}

        <Card className="mb-3 border border-gray-200 transition-colors hover:border-[#C84648]">
          <div className="flex items-center justify-between p-2">
            <Checkbox
              style={{ color: '#C84648' }}
              defaultChecked={isCancelled}
              onChange={(e) =>
                handleSetCancellationList(shift.opening_id, e.target.checked)
              }
              disabled={cannotCancel}
              className="mr-2"
            />
            {!isCancelled && shift.assigned_clinician ? (
              <div className="flex items-center space-x-2">
                <Avatar
                  color="blue"
                  placeholderInitials={shift.clinician_name?.[0] || ''}
                  size="sm"
                />
                <Text className="text-sm">{shift.clinician_name}</Text>
              </div>
            ) : (
              <Text className="text-sm">
                {(shift.selected_clinician_application?.length || 0) +
                  (shift.other_applications?.length || 0)}{' '}
                applications
              </Text>
            )}
            <Badge color="zinc" className="text-xs">
              <span>{getDisplayStatus(shift.opening_status)}</span>
            </Badge>
          </div>
        </Card>
      </Modal.Body>

      <Modal.Footer className="border-t border-gray-200 bg-[#FFEAEA] p-3">
        <Button
          onClick={handleProcessCancellations}
          disabled={cannotCancel || cancellationList.length === 0 || isCancelling}
          className="w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:shadow-lg hover:shadow-red-500/25 active:scale-95 sm:w-auto"
          style={{ backgroundColor: '#C84648', border: 'none' }}
        >
          {isCancelling ? (
            <svg
              className="size-4 animate-spin text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            'Confirm Cancellation'
          )}
        </Button>
        <Button
          color="gray"
          onClick={onClose}
          className="mt-2 w-full rounded-lg px-4 py-1.5 text-sm sm:ml-2 sm:mt-0 sm:w-auto"
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/* --------------------------------------------------------------- */
const ShiftDetailView: React.FC = () => {
  const { shiftId, openingId } = useParams<{
    shiftId: string;
    openingId: string;
  }>();
  const navigate = useNavigate();

  /* ------------------- State ------------------- */
  const [shiftData, setShiftData] = useState<ShiftDetailsForFacilityResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const api = useApiService();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [selectedClinicianId, setSelectedClinicianId] = useState<string | null>(null);

  const [editingClockIn, setEditingClockIn] = useState(false);
  const [editingClockOut, setEditingClockOut] = useState(false);
  const [clockInValue, setClockInValue] = useState('');
  const [clockOutValue, setClockOutValue] = useState('');
  const [isUpdatingClock, setIsUpdatingClock] = useState(false);
  const [clockUpdateSuccess, setClockUpdateSuccess] = useState<string | null>(null);

  const { mutate: createShift, isLoading: isCreating } = useCreate<CreateNewShift>();
  const [clinicianProfileData, setClinicianProfileData] = useState<{ data: IClinicianProfile } | null>(null);

  /* ------------------- Data fetching ------------------- */
  const fetchShiftDetails = useCallback(async () => {
    if (!shiftId || !openingId) {
      setError('Missing shiftId or openingId');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<ShiftDetailsForFacilityResponse>(
        `/shifts/get-shift-details-for-facility?shiftId=${shiftId}&openingId=${openingId}`
      );
      setShiftData(response.data);
    } catch {
      setError('Failed to load shift details');
      setShiftData(null);
    } finally {
      setIsLoading(false);
    }
  }, [shiftId, openingId, api]);

  useEffect(() => {
    fetchShiftDetails();
  }, [fetchShiftDetails]);

  const refetchShiftDetails = useCallback(() => fetchShiftDetails(), [fetchShiftDetails]);

  const fetchClinicianProfile = useCallback(
    async (clinicianId: string) => {
      if (!clinicianId) return;
      try {
        const response = await api.get<IClinicianProfile>(
          `/clinicians/clinician-profile?clinicianId=${clinicianId}&isFacilityUser=true`
        );
        setClinicianProfileData({ data: response.data });
      } catch {
        setClinicianProfileData(null);
      }
    },
    [api]
  );

  useEffect(() => {
    if (selectedClinicianId) fetchClinicianProfile(selectedClinicianId);
  }, [selectedClinicianId, fetchClinicianProfile]);

  /* ------------------- Handlers ------------------- */
  const handlePostShift = (shiftData: Partial<CreateNewShift>) => {
    createShift(
      { resource: 'shifts', values: shiftData },
      { onSuccess: () => setIsModalOpen(false) }
    );
  };

  const goBack = () => navigate(-1);
  const handleApplicationClick = (clinicianId: string) => {
    setSelectedClinicianId(clinicianId);
    setIsProfileModalOpen(true);
  };
  const handleUpdateShift = () => refetchShiftDetails();

  /* ------------------- Clock-in/out logic ------------------- */
  const canEditClockIn = () => {
    const status = shiftData?.opening_status;
    const disabled = ['CANCELLED', 'EXPIRED', 'SELECTED'];
    if (disabled.includes(status!)) return false;
    if (!shiftData?.start_time) return false;
    const now = new Date();
    const shiftStart = parseISO(shiftData.start_time);
    const thirtyMinsBefore = subMinutes(shiftStart, 30);
    return isAfter(now, thirtyMinsBefore);
  };

  const getTimeUntilClockInEnabled = () => {
    const status = shiftData?.opening_status;
    const disabled = ['CANCELLED', 'EXPIRED', 'SELECTED'];
    if (disabled.includes(status!)) return null;
    if (!shiftData?.start_time) return null;
    const now = new Date();
    const shiftStart = parseISO(shiftData.start_time);
    const thirtyMinsBefore = subMinutes(shiftStart, 30);
    if (isAfter(now, thirtyMinsBefore)) return null;
    const diff = thirtyMinsBefore.getTime() - now.getTime();
    const h = Math.floor(diff / (1000 * 60 * 60));
    const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const canEditClockOut = () => {
    const s = shiftData?.opening_status;
    return ['COMPLETED', 'MISSED_CLOCK_IN', 'MISSED_CLOCK_OUT', 'NO_SHOW', 'UNRESOLVED', 'CLOCKED_IN'].includes(s!);
  };

  const handleSaveClockIn = async () => {
    if (!shiftData || !clockInValue) return;

    /* validation */
    try {
      const effectiveOut = shiftData.reported_clock_out || shiftData.immutable_clock_out || shiftData.end_time;
      if (effectiveOut) {
        const ci = parseISO(clockInValue);
        const co = parseISO(effectiveOut);
        if (ci > co) {
          setError('Clock-in time cannot be after clock-out time.');
          return;
        }
      }
    } catch {
      setError('Failed to update clock in time.');
      return;
    }

    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(shiftData.opening_id) || !uuidRegex.test(shiftData.clinician_id!)) return;

    setIsUpdatingClock(true);
    setError(null);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let newStatus = shiftData.opening_status;
      if (['MISSED_CLOCK_IN', 'NO_SHOW'].includes(shiftData.opening_status)) newStatus = 'CLOCKED_IN';
      else if (shiftData.opening_status === 'CONFIRMED') newStatus = 'CLOCKED_IN';

      const payload = {
        openingId: shiftData.opening_id,
        clinicianId: shiftData.clinician_id,
        timezone: tz,
        immutable_clock_in: clockInValue,
        clock_in: clockInValue,
        status: newStatus,
      };

      const response = await api.post<any>('/shifts/update-shift-time', payload);
      const reported = response.data?.immutable_clock_in ?? response.data?.clock_in;
      const status = response.data?.status ?? newStatus;

      setShiftData((prev) =>
        prev ? { ...prev, reported_clock_in: reported, opening_status: status } : null
      );

      setEditingClockIn(false);
      setClockInValue('');
      setClockUpdateSuccess('Clock in time updated successfully!');
      setTimeout(() => setClockUpdateSuccess(null), 3000);
      await refetchShiftDetails();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Failed to update clock in time.');
    } finally {
      setIsUpdatingClock(false);
    }
  };

  const handleSaveClockOut = async () => {
    if (!shiftData || !clockOutValue) return;

    try {
      const effectiveIn = shiftData.reported_clock_in || shiftData.immutable_clock_in || shiftData.start_time;
      if (effectiveIn) {
        const ci = parseISO(effectiveIn);
        const co = parseISO(clockOutValue);
        if (co < ci) {
          setError('Clock-out time cannot be earlier than clock-in time.');
          return;
        }
      }
    } catch {
      setError('Failed to update clock out time.');
      return;
    }

    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(shiftData.opening_id) || !uuidRegex.test(shiftData.clinician_id!)) return;

    setIsUpdatingClock(true);
    setError(null);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let newStatus = shiftData.opening_status;
      if (['CLOCKED_IN', 'MISSED_CLOCK_OUT'].includes(shiftData.opening_status)) newStatus = 'COMPLETED';
      else if (shiftData.opening_status === 'NO_SHOW' && shiftData.immutable_clock_in) newStatus = 'COMPLETED';

      const payload = {
        openingId: shiftData.opening_id,
        clinicianId: shiftData.clinician_id,
        timezone: tz,
        immutable_clock_out: clockOutValue,
        clock_out: clockOutValue,
        status: newStatus,
      };

      const response = await api.post<any>('/shifts/update-shift-time', payload);
      const reported = response.data?.immutable_clock_out ?? response.data?.clock_out;
      const status = response.data?.status ?? newStatus;

      setShiftData((prev) =>
        prev ? { ...prev, reported_clock_out: reported, opening_status: status } : null
      );

      setEditingClockOut(false);
      setClockOutValue('');
      setClockUpdateSuccess('Clock out time updated successfully!');
      setTimeout(() => setClockUpdateSuccess(null), 3000);
      await refetchShiftDetails();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Failed to update clock out time.');
    } finally {
      setIsUpdatingClock(false);
    }
  };

  /* ------------------- Render helpers ------------------- */
  if (isLoading) return <Loading />;
  if (!shiftData) return <p className="text-center text-gray-400">Shift not found.</p>;

  const shiftStartPassed = isShiftStartPassed(shiftData);

  const positionStyle = getPositionStyles(shiftData.position_title || 'Unknown');

  // FIXED: second argument was missing
  const shiftDuration = differenceInHours(
    parseISO(shiftData.end_time),
    parseISO(shiftData.start_time)
  );

  const getDisplayStatusForOpening = () => {
    const base = shiftData.opening_status;
    if (base === 'ACTIVE') {
      const all = [
        ...(shiftData.selected_clinician_application || []),
        ...(shiftData.other_applications || []),
      ];
      if (hasApplicants(all)) return 'Applicants Applied';
    }
    return getDisplayStatus(base);
  };

  const displayStatus = getDisplayStatusForOpening();

  const isShiftCancelled = shiftData.opening_status === 'CANCELLED';
  const isShiftExpired = shiftData.opening_status === 'EXPIRED';
  const isShiftInProgress =
    shiftData.opening_status === 'CLOCKED_IN' ||
    shiftData.opening_status?.toLowerCase().includes('progress');
  const isShiftWorked = shiftData.opening_status === 'COMPLETED';
  // const now = new Date(); // Already defined in isShiftStartPassed
  // const shiftStartTime = parseISO(shiftData.start_time); // Already defined in isShiftStartPassed
  // const isShiftInPast = isBefore(shiftStartTime, now); // Use shiftStartPassed directly
  const cannotCancelShift =
    isShiftCancelled || isShiftExpired || isShiftInProgress || isShiftWorked || shiftStartPassed;


  // NEW LOGIC FOR MESSAGE AND BUTTON DISABLING
  const statusesForDisablement = ['ACTIVE', 'APPLICATION_APPLIED', 'SELECTED']; // 'ACTIVE' usually means 'Open'
  const shouldDisableApplicationsAndShowMessage =
    shiftStartPassed &&
    statusesForDisablement.includes(shiftData.opening_status);

  const clinicianShift: IClinicianShift = {
    id: shiftData.shift_id,
    email: '',
    status: shiftData.opening_status,
    shift_id: shiftData.shift_id,
    immutable_clock_in: shiftData.reported_clock_in,
    immutable_clock_out: shiftData.reported_clock_out,
    reported_clock_in: shiftData.reported_clock_in,
    reported_clock_out: shiftData.reported_clock_out,
    date: shiftData.date,
    start_time: shiftData.start_time,
    end_time: shiftData.end_time,
    description: shiftData.description,
    position_title: shiftData.position_title,
    clinician_id: selectedClinicianId,
    base_rate: parseFloat(shiftData.base_rate) || 0,
    rate_margin: parseFloat(shiftData.rate_margin) || 0,
  };

  const clinicianProfile: IClinicianProfile | null = clinicianProfileData?.data
    ? {
        ...clinicianProfileData.data,
        position_id: clinicianProfileData.data.position_id || '',
        is_approved: clinicianProfileData.data.is_approved ?? false,
        document_status_summary: {
          ...defaultDocumentStatusSummary,
          ...(clinicianProfileData.data.document_status_summary || {}),
        },
        people: clinicianProfileData.data.people,
        documents: clinicianProfileData.data.documents || [],
      }
    : null;

  /* --------------------------------------------------------------- */
  return (
    <div className="min-h-screen" style={{ backgroundColor: semanticColors.background.primary }}>
      {/* ---------- Header ---------- */}
      <div className="relative overflow-hidden rounded-xl" style={{ background: semanticColors.background.gradient }}>
        <div className="absolute inset-0 bg-black/10 rounded-xl"></div>
        <div className="relative px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-6">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center sm:gap-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={goBack}
                  className="group flex items-center rounded-lg bg-white/10 backdrop-blur-sm px-3 py-2 text-sm font-medium text-white/90 transition-all duration-200 hover:bg-white/20 hover:text-white hover:shadow-lg hover:shadow-white/10 active:scale-95 border border-white/20"
                >
                  <ChevronLeftIcon className="size-4 text-white/80 transition-transform duration-200 group-hover:-translate-x-1 group-hover:text-white" />
                  <span className="ml-2 font-semibold">Back</span>
                </button>
                <div className="space-y-1">
                  <h1
                    className="text-2xl font-bold sm:text-3xl lg:text-2xl drop-shadow-sm"
                    style={{
                      background: 'linear-gradient(to right, rgb(245, 185, 87), rgb(255, 237, 205), rgb(171, 109, 10))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    Shift Details
                  </h1>
                  <p className="text-sm text-white/90 sm:text-base lg:text-sm font-medium drop-shadow-sm">
                    {shiftData.position_title || 'Unknown'} - {shiftData.facility_name || 'N/A Facility'}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="rounded-lg bg-white/20 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/30 sm:px-4 sm:py-2 sm:text-sm lg:px-3 lg:py-1.5 lg:text-xs"
                  // Use the new variable for disabling
                  disabled={isShiftCancelled || shouldDisableApplicationsAndShowMessage}
                  title={shouldDisableApplicationsAndShowMessage ? 'Cannot post shift after start time for this status' : undefined}
                >
                  Post Shift
                </button>

                <button
                  onClick={() => setIsCancelModalOpen(true)}
                  className="rounded-lg bg-white/20 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/30 sm:px-4 sm:py-2 sm:text-sm lg:px-3 lg:py-1.5 lg:text-xs"
                  style={{ backgroundColor: '#C84648', border: 'none' }}
                  disabled={cannotCancelShift}
                  title={
                    cannotCancelShift
                      ? isShiftCancelled
                        ? 'Shift is already cancelled'
                        : isShiftExpired
                        ? 'Cannot cancel an expired shift'
                        : isShiftInProgress
                        ? 'Cannot cancel shift in progress'
                        : isShiftWorked
                        ? 'Cannot cancel completed shift'
                        : shiftStartPassed
                        ? 'Cannot cancel a shift that has already started' // Use shiftStartPassed here
                        : 'Cannot cancel this shift'
                      : 'Cancel this shift'
                  }
                >
                  Cancel Shift
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Banner when start time passed ---------- */}
      {/* Use the new variable here */}
      {shouldDisableApplicationsAndShowMessage && (
        <div className="mx-auto max-w-6xl px-3 sm:px-4 lg:px-3 mt-4">
          <Alert
            color="warning"
            className="border border-amber-300 bg-amber-50 text-amber-800"
            icon={ExclamationTriangleIcon}
          >
            <span className="font-medium">
              The shift start time has already passed, so selection isn’t possible. Please post a new shift.
            </span>
          </Alert>
        </div>
      )}

      <main className="mx-auto max-w-6xl p-3 sm:p-4 lg:p-3 overflow-hidden">
        {/* ---------- Cancelled shift banner ---------- */}
        {isShiftCancelled && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 shadow">
            <Alert color="red" className="mb-3">
              <span className="text-sm font-semibold text-red-800">
                This shift is cancelled. No further actions are allowed.
              </span>
            </Alert>
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold text-gray-900">{shiftData.position_title || 'Unknown'}</h2>
              <Badge color="red" className="text-sm"><span>CANCELLED</span></Badge>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8 w-full">
          {/* ---------- Left column ---------- */}
          <div className="space-y-6 lg:col-span-2 min-w-0">
            {/* Shift info card */}
            <Card className="rounded-xl bg-white p-4 shadow-lg sm:p-6 lg:p-4">
              <div className={` ${positionStyle} mb-4 pb-4`}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center space-x-3">
                    <span className={`rounded-full px-3 py-1 text-sm font-semibold ${positionStyle.badge}`}>
                      {shiftData.position_title?.toUpperCase() || 'UNKNOWN'}
                    </span>
                    <h2 className="text-lg font-semibold text-gray-800 md:text-xl">
                      {shiftData.facility_name || 'N/A Facility'}
                    </h2>
                  </div>
                  <span
                    className="mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium sm:mt-0"
                    style={{
                      backgroundColor:
                        displayStatus === 'Confirmed' ? '#E0EDE2' :
                        displayStatus === 'Open' ? '#E0E8ED' :
                        displayStatus === 'Cancelled' ? '#FFEAEA' :
                        displayStatus === 'Selected' ? '#FFF8E6' :
                        displayStatus === 'Worked' ? '#E0EDE2' :
                        displayStatus === 'Expired' ? '#FFF8E6' :
                        displayStatus === 'No Show' ? '#FFEAEA' :
                        displayStatus === 'Unresolved' ? '#FFE4CC' :
                        displayStatus === 'Applicants Applied' ? '#E0E8ED' :
                        displayStatus === 'In Progress' ? '#E0EDE2' : '#F3F4F6',
                      color:
                        displayStatus === 'Confirmed' ? '#3A7D44' :
                        displayStatus === 'Open' ? '#3A5F7D' :
                        displayStatus === 'Cancelled' ? '#C84648' :
                        displayStatus === 'Selected' ? '#D4A123' :
                        displayStatus === 'Worked' ? '#3A7D44' :
                        displayStatus === 'Expired' ? '#D4A123' :
                        displayStatus === 'No Show' ? '#C84648' :
                        displayStatus === 'Unresolved' ? '#E67E22' :
                        displayStatus === 'Applicants Applied' ? '#3A5F7D' :
                        displayStatus === 'In Progress' ? '#3A7D44' : '#6B7280',
                    }}
                    title={displayStatus}
                  >
                    {displayStatus}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="flex items-start space-x-3">
                  <CalendarDaysIcon className="size-6 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Date</p>
                    <p className="text-base font-medium text-gray-900">
                      {format(parseISO(shiftData.start_time), 'EEEE, MMMM d, yyyy')}
                    </p>
                  </div>
                </div>

                
                <div className="flex items-start space-x-3">
                  <ClockIcon className="size-6 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Time</p>
                    <p className="text-base font-medium text-gray-900">
                      {formatShiftTime(shiftData.start_time)} -{' '}
                      {formatShiftTime(shiftData.end_time)} ({shiftDuration} hrs)
                    </p>
                    <p className="text-sm text-gray-500">Break: {shiftData.break_time} mins</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <UsersIcon className="size-6 text-gray-400" />
                  <div>
                    {(() => {
                      const total = [
                        ...(shiftData.selected_clinician_application || []),
                        ...(shiftData.other_applications || []),
                      ].length;
                      return total > 0 ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Total applications:</span>
                          <span className="text-sm font-medium text-gray-900">{total}</span>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <p className="mb-2 text-sm text-gray-500">Description</p>
                  <p className="whitespace-pre-wrap text-base text-gray-700 break-words">
                    {shiftData.description || 'No description provided.'}
                  </p>
                  <p className="mt-4 mb-2 text-sm text-gray-500">Instructions</p>
                  <p className="whitespace-pre-wrap text-base text-gray-700 break-words">
                    {shiftData.instructions || 'No instructions provided.'}
                  </p>
                </div>
              </div>
            </Card>

            {/* ---------- Selected Applications ---------- */}
            {!isShiftCancelled && shiftData.selected_clinician_application?.length ? (
              <Card className="rounded-xl bg-white p-4 shadow-lg sm:p-6 lg:p-4">
                <div className="mb-4 flex items-center justify-between border-b pb-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-base font-semibold text-gray-900 sm:text-lg lg:text-base">
                      Selected Applications
                    </h3>
                    <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700">
                      {shiftData.selected_clinician_application.length}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  {shiftData.selected_clinician_application.map((app) => {
                    const mapped = {
                      ...app,
                      clinicians: {
                        clinician_id: app.clinician_id,
                        person_id: '',
                        status: app.status,
                        people: {
                          person_id: '',
                          firstname: app.clinician_name.split(' ')[0] ?? '',
                          lastname: app.clinician_name.split(' ').slice(1).join(' ') ?? '',
                          email: '',
                          photo: undefined,
                        },
                        reliability: Number.isFinite(parseFloat(app.reliability_score))
                          ? Math.round(parseFloat(app.reliability_score))
                          : undefined,
                      },
                    };
                    return (
                      <ApplicationRow
                        key={app.application_id}
                        application={mapped}
                        refetchData={refetchShiftDetails}
                        onApplicationClick={handleApplicationClick}
                        openingId={shiftData.opening_id}
                        showRemove
                        hasSelectedApplication={shiftData.selected_clinician_application.length > 0}
                        isSelectedApplication
                        shiftStatus={shiftData.opening_status}
                        isShiftStartPassed={shiftStartPassed}
                        // NEW PROP: Pass the disablement flag
                        disableApplicationActions={shouldDisableApplicationsAndShowMessage}
                      />
                    );
                  })}
                </div>
              </Card>
            ) : null}

            {/* ---------- Other Applications ---------- */}
            {!isShiftCancelled && shiftData.other_applications?.length ? (
              <Card className="rounded-xl bg-white p-4 shadow-lg sm:p-6 lg:p-4">
                <div className="mb-4 flex items-center justify-between border-b pb-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-base font-semibold text-gray-900 sm:text-lg lg:text-base">
                      Other Applications
                    </h3>
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700">
                      {shiftData.other_applications.length}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  {shiftData.other_applications.map((app) => {
                    const mapped = {
                      ...app,
                      clinicians: {
                        clinician_id: app.clinician_id,
                        person_id: '',
                        status: app.status,
                        people: {
                          person_id: '',
                          firstname: app.clinician_name?.split(' ')[0] ?? '',
                          lastname: app.clinician_name?.split(' ').slice(1).join(' ') ?? '',
                          email: '',
                          photo: undefined,
                        },
                        reliability: Number.isFinite(parseFloat(app.reliability_score))
                          ? Math.round(parseFloat(app.reliability_score))
                          : undefined,
                      },
                    };
                    const hasRejectedSelected = shiftData.selected_clinician_application?.some(
                      (s) => s.status === 'REJECTED'
                    );
                    return (
                      <ApplicationRow
                        key={app.application_id}
                        application={mapped}
                        refetchData={refetchShiftDetails}
                        onApplicationClick={handleApplicationClick}
                        openingId={shiftData.opening_id}
                        showRemove={false}
                        hasSelectedApplication={
                          shiftData.selected_clinician_application.length > 0 && !hasRejectedSelected
                        }
                        isSelectedApplication={false}
                        shiftStatus={shiftData.opening_status}
                        isShiftStartPassed={shiftStartPassed}
                        // NEW PROP: Pass the disablement flag
                        disableApplicationActions={shouldDisableApplicationsAndShowMessage}
                      />
                    );
                  })}
                </div>
              </Card>
            ) : null}

            {/* ---------- No Applications ---------- */}
            {!isShiftCancelled &&
              (!shiftData.selected_clinician_application?.length && !shiftData.other_applications?.length) && (
                <Card className="rounded-xl bg-white p-4 shadow-lg sm:p-6 lg:p-4">
                  <div className="py-8 text-center">
                    <UsersIcon className="mx-auto size-12 text-gray-300 mb-3" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications</h3>
                    <p className="text-sm text-gray-500">
                      No clinicians have applied for this shift yet.
                    </p>
                  </div>
                </Card>
              )}
          </div>

          {/* ---------- Right column – Assigned Clinician ---------- */}
          <div className="lg:col-span-1 min-w-0">
            {!isShiftCancelled && shiftData.clinician_name && (
              <Card className="rounded-xl bg-white p-4 shadow-lg sm:p-6 lg:p-4">
                <div className="mb-4 flex items-center justify-between border-b pb-4">
                  <div className="flex items-center">
                    <UsersIcon className="mr-3 size-5 text-gray-400 sm:size-6 lg:size-5" />
                    <h3 className="text-base font-bold text-gray-900 sm:text-lg lg:text-base">
                      Assigned Clinician
                    </h3>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Avatar
                      color="blue"
                      placeholderInitials={shiftData.clinician_name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')}
                      size="lg"
                    />
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900">{shiftData.clinician_name}</h4>
                      <div className="mt-1">
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                          style={{
                            backgroundColor:
                              displayStatus === 'Confirmed' ? '#E0EDE2' :
                              displayStatus === 'Open' ? '#E0E8ED' :
                              displayStatus === 'Cancelled' ? '#FFEAEA' :
                              displayStatus === 'Selected' ? '#FFF8E6' :
                              displayStatus === 'Worked' ? '#E0EDE2' :
                              displayStatus === 'Expired' ? '#FFF8E6' :
                              displayStatus === 'No Show' ? '#FFEAEA' :
                              displayStatus === 'Unresolved' ? '#FFE4CC' :
                              displayStatus === 'Applicants Applied' ? '#E0E8ED' :
                              displayStatus === 'In Progress' ? '#E0EDE2' : '#F3F4F6',
                            color:
                              displayStatus === 'Confirmed' ? '#3A7D44' :
                              displayStatus === 'Open' ? '#3A5F7D' :
                              displayStatus === 'Cancelled' ? '#C84648' :
                              displayStatus === 'Selected' ? '#D4A123' :
                              displayStatus === 'Worked' ? '#3A7D44' :
                              displayStatus === 'Expired' ? '#D4A123' :
                              displayStatus === 'No Show' ? '#C84648' :
                              displayStatus === 'Unresolved' ? '#E67E22' :
                              displayStatus === 'Applicants Applied' ? '#3A5F7D' :
                              displayStatus === 'In Progress' ? '#3A7D44' : '#6B7280',
                          }}
                        >
                          {displayStatus}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center mb-3">
                      <ClockIcon className="mr-2 size-4 text-gray-400" />
                      <h4 className="text-sm font-semibold text-gray-900">Clock Information</h4>
                    </div>

                    {clockUpdateSuccess && (
                      <div className="mb-3 rounded-lg bg-green-50 border border-green-200 p-3">
                        <div className="flex items-center">
                          <CheckIcon className="size-4 text-green-600 mr-2" />
                          <span className="text-sm text-green-800 font-medium">{clockUpdateSuccess}</span>
                        </div>
                      </div>
                    )}
                    {error && (
                      <div className="mb-3 rounded-lg bg-red-50 border border-red-200 p-3">
                        <div className="flex items-center">
                          <ExclamationTriangleIcon className="size-4 text-red-600 mr-2" />
                          <span className="text-sm text-red-800 font-medium">{error}</span>
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      {/* Clock In */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Clock In</span>
                        </div>
                        {editingClockIn ? (
                          <div className="space-y-2">
                            <input
                              type="datetime-local"
                              value={clockInValue ? format(parseISO(clockInValue), "yyyy-MM-dd'T'HH:mm") : ''}
                              onChange={(e) => setClockInValue(e.target.value)}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={isUpdatingClock}
                              min={format(parseISO(shiftData.start_time), "yyyy-MM-dd'T'HH:mm")}
                              max={format(addDays(parseISO(shiftData.start_time), 1), "yyyy-MM-dd'T'HH:mm")}
                            />
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={handleSaveClockIn}
                                disabled={isUpdatingClock || !clockInValue}
                                className="flex flex-1 items-center justify-center space-x-1 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:bg-gray-300"
                              >
                                {isUpdatingClock ? (
                                  <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                ) : (
                                  <CheckIcon className="size-4" />
                                )}
                                <span>Save</span>
                              </button>
                              <button
                                onClick={() => {
                                  setEditingClockIn(false);
                                  setClockInValue('');
                                }}
                                disabled={isUpdatingClock}
                                className="flex flex-1 items-center justify-center space-x-1 rounded-lg bg-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-400"
                              >
                                <XMarkIcon className="size-4" />
                                <span>Cancel</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col space-y-2">
                            <div className="flex items-center space-x-2">
                              {(() => {
                                const ci = shiftData.reported_clock_in || shiftData.immutable_clock_in;
                                return (
                                  <span
                                    className={`text-sm font-medium ${ci ? 'text-gray-900' : 'text-gray-500'}`}
                                  >
                                    {ci ? format(parseISO(ci), 'MMM d, yyyy h:mm a') : 'N/A'}
                                  </span>
                                );
                              })()}
                              {shiftData.reported_clock_in && (
                                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                  Updated
                                </span>
                              )}
                              <button
                                onClick={() => {
                                  const val = shiftData.reported_clock_in || format(parseISO(shiftData.start_time), "yyyy-MM-dd'T'HH:mm");
                                  setClockInValue(val);
                                  setEditingClockIn(true);
                                }}
                                disabled={isUpdatingClock || !canEditClockIn()}
                                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={
                                  !canEditClockIn()
                                    ? (() => {
                                        const s = shiftData.opening_status;
                                        const disabled = ['CANCELLED', 'EXPIRED', 'SELECTED'];
                                        if (disabled.includes(s!)) return `Clock-in disabled for ${s?.toLowerCase()} shifts`;
                                        const t = getTimeUntilClockInEnabled();
                                        return t ? `Clock-in available in ${t}` : 'Clock-in not available';
                                      })()
                                    : 'Edit clock in time'
                                }
                              >
                                <PencilIcon className="size-3 text-gray-500" />
                              </button>
                            </div>

                            {!canEditClockIn() && shiftData.opening_status && (() => {
                              const s = shiftData.opening_status;
                              const disabled = ['CANCELLED', 'EXPIRED', 'SELECTED'];
                              if (disabled.includes(s!))
                                return (
                                  <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded-md">
                                    Clock-in is disabled for {s?.toLowerCase()} shifts
                                  </div>
                                );
                              const t = getTimeUntilClockInEnabled();
                              if (t)
                                return (
                                  <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-md">
                                    Clock in will be available 30 minutes before the shift starts
                                  </div>
                                );
                              return null;
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Clock Out */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Clock Out</span>
                        </div>
                        {editingClockOut ? (
                          <div className="space-y-2">
                            <input
                              type="datetime-local"
                              value={clockOutValue ? format(parseISO(clockOutValue), "yyyy-MM-dd'T'HH:mm") : ''}
                              onChange={(e) => setClockOutValue(e.target.value)}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={isUpdatingClock}
                              min={format(parseISO(shiftData.start_time), "yyyy-MM-dd'T'HH:mm")}
                              max={format(addDays(parseISO(shiftData.start_time), 1), "yyyy-MM-dd'T'HH:mm")}
                            />
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={handleSaveClockOut}
                                disabled={isUpdatingClock || !clockOutValue}
                                className="flex flex-1 items-center justify-center space-x-1 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:bg-gray-300"
                              >
                                {isUpdatingClock ? (
                                  <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                ) : (
                                  <CheckIcon className="size-4" />
                                )}
                                <span>Save</span>
                              </button>
                              <button
                                onClick={() => {
                                  setEditingClockOut(false);
                                  setClockOutValue('');
                                }}
                                disabled={isUpdatingClock}
                                className="flex flex-1 items-center justify-center space-x-1 rounded-lg bg-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-400"
                              >
                                <XMarkIcon className="size-4" />
                                <span>Cancel</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            {(() => {
                              const co = shiftData.reported_clock_out || shiftData.immutable_clock_out;
                              return (
                                <span
                                  className={`text-sm font-medium ${co ? 'text-gray-900' : 'text-gray-500'}`}
                                >
                                  {co ? format(parseISO(co), 'MMM d, yyyy h:mm a') : 'N/A'}
                                </span>
                              );
                            })()}
                            {shiftData.reported_clock_out && (
                              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                Updated
                              </span>
                            )}
                            <button
                              onClick={() => {
                                const val = shiftData.reported_clock_out || format(parseISO(shiftData.end_time), "yyyy-MM-dd'T'HH:mm");
                                setClockOutValue(val);
                                setEditingClockOut(true);
                              }}
                              disabled={isUpdatingClock || !canEditClockOut()}
                              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Edit clock out time"
                            >
                              <PencilIcon className="size-3 text-gray-500" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      <PostShiftModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handlePostShift}
        isSubmitting={isCreating}
      />
      <ShiftModal
        open={isProfileModalOpen}
        handleClose={() => {
          setIsProfileModalOpen(false);
          setSelectedClinicianId(null);
        }}
        shift={clinicianShift}
        clinicianProfile={clinicianProfile}
        onUpdateShift={handleUpdateShift}
      />
      <ShiftCancellationModal
        open={isCancelModalOpen}
        setOpen={setIsCancelModalOpen}
        shift={shiftData}
        refetchData={refetchShiftDetails}
      />
    </div>
  );
};

export default ShiftDetailView;